import { backToNative, setNavigationBar, togglePullToRefresh, startAddDevice, isInApp } from '@docs/common'
import { useAsyncEffect, useDocumentVisibility } from 'ahooks'
import { Swiper, SwiperRef } from 'antd-mobile'
import clsx from 'classnames'
import { getTutorialDomain, triggerLink } from 'common/util'
import { round, uniq, some } from 'lodash'
import qs from 'qs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'
import getDocument, { IDocument, IAction } from 'servers/getDocument'
import getDevList, { IDevice } from 'servers/getDevsList'
import { getLikeStatus, likeTutorialDetail, unlikeTutorialDetail } from 'servers/like'
import {
  trackDetailBack,
  trackDetailButton,
  trackDetailLike,
  trackDetailLinkClick,
  trackDetailLinksSheet,
  trackDetailUnLike,
  trackDetailViewPercent,
  trackDetailPageView,
} from '../../common/track'

import ActionSheet from 'components/ActionSheet'
import CKEContent from 'components/CKEContent'
import Video from 'components/Video'
import NativeVideo from 'components/NativeVideo'
import { isYouTubeVideo } from '../../utils/videoUtils'

import styles from './index.module.scss'
import useAraLayout from 'common/useAraLayout'

enum Actions {
  startAddDevice = 'startAddDevice',
}

enum Condition {
  HasBindDevice = 'hasBindDevice',
}

const filterButtons = (buttons: IAction[], devList: IDevice[]) => {
  return buttons.filter(btn => {
    const visible = btn.extra?.visible
    if (visible?.condition === Condition.HasBindDevice) {
      return HasBindDevice(btn, devList)
    }
    return true
  })
}

const HasBindDevice = (btn: IAction, devList: IDevice[]) => {
  const { fullfill, params } = btn.extra?.visible
  const isFullfilled = some(devList, o => o.device_sn === params)
  return isFullfilled ? fullfill : !fullfill
}

const DetailPage = () => {
  const [document, setDocument] = useState<IDocument>()
  const [linksSheetVisible, setLinksSheetVisible] = useState(false)
  const [currentActions, setCurrentActions] = useState<IAction[]>([])
  const [devList, setDevList] = useState<IDevice[]>([])
  const [liked, setLiked] = useState(0)
  const [stepIndex, setStepIndex] = useState(0)
  useAraLayout()

  const { id } = useParams<{ id: string }>()
  const { entry_id, pn, app, uid, hideNavBar } = qs.parse(window.location.search, { ignoreQueryPrefix: true }) as {
    [key: string]: string
  }

  // soundcore 暂不支持 like 功能
  const shouldShowLike = useMemo(() => app !== 'soundcore', [app])

  const scrollContanier = useRef<HTMLDivElement>(null)
  // const scroller = useScroll(scrollContanier)
  const containerSwiper = useRef<SwiperRef>(null)
  const bannerSwiper = useRef<SwiperRef>(null)
  // start_ai_generated
  const contentDom = useRef<HTMLDivElement>(null)
  // end_ai_generated
  const stepsRef = useRef<HTMLDivElement>(null)
  const viewedSteps = useRef<number[]>([1])

  const pageVisible = useDocumentVisibility()

  const bindDeviceButtons = useMemo(() => {
    return document?.steps
      .flatMap(item => item.buttons || [])
      .filter(item => some(item.extra?.visible, o => o.condition === Condition.HasBindDevice))
  }, [document?.steps])

  const filterSteps = useMemo(() => {
    return document?.steps.map(step => {
      const buttons = filterButtons(step.buttons, devList)
      return { ...step, buttons }
    })
  }, [devList, document?.steps])


  const handleBannerSwipe = (index: number) => {
    setStepIndex(index)
  }

  const handleStepItemClick = (index: number) => {
    setStepIndex(index)
  }

  const handleButtonClick = (action: IAction) => {
    trackDetailButton(uid || id, action.title || '', entry_id as string)
    if (action.extra?.action === Actions.startAddDevice) {
      startAddDevice()
      return
    }
    triggerLink(action.url || '')
  }

  const handleLinksSheetClick = (actions: IAction[]) => {
    setLinksSheetVisible(true)
    trackDetailLinksSheet(uid || id)
    setCurrentActions(actions)
  }

  const closeLinksSheetVisible = () => {
    setLinksSheetVisible(false)
  }

  const handleLike = async () => {
    if (!(await isInApp()) && /eufy[ _-]?security/i.test(app)) {
        const url = `eufysecurity://eufy/explore/web#${await getTutorialDomain()}/detail/${id}?pn=${pn}&entry_id=${entry_id}&toApp=eufy-security&uid=${uid}`
        triggerLink(url)
        return
    }
    if (liked) {
      setLiked(0)
      trackDetailUnLike(uid || id)
      await unlikeTutorialDetail(+id)
    } else {
      trackDetailLike(uid || id)
      setLiked(1)
      await likeTutorialDetail(+id)
    }
  }

  const trackViewPercent = useCallback(() => {
    const length = document?.steps?.length || 1
    const percent = round((viewedSteps.current.length / length) * 100, 1) + '%'
    trackDetailViewPercent(uid || id, percent)
  }, [document?.steps?.length, uid, id])

  useEffect(() => {
    if (pageVisible === 'hidden') {
      trackViewPercent()
    }
  }, [pageVisible, trackViewPercent])

  useEffect(() => {
    const viewed = [...viewedSteps.current, stepIndex + 1]
    viewedSteps.current = uniq(viewed)
    bannerSwiper.current?.swipeTo(stepIndex)
    containerSwiper.current?.swipeTo(stepIndex)
    // 让当前点击的元素居中显示在横向滚动条中间
    const item = stepsRef.current?.querySelectorAll(`.${styles.item}`)[stepIndex]
    if (item) {
      const length = document?.steps?.length || 1

      if (stepsRef.current) {
        const scrollWidth = stepsRef.current.scrollWidth
        const scrollStep = scrollWidth / length

        stepsRef.current.scrollTo({
          left: scrollStep * (stepIndex - 1),
          behavior: 'smooth',
        })
      }
      scrollContanier.current?.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
      // start_ai_generated
      contentDom.current?.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
      // end_ai_generated
    }
  }, [document?.steps?.length, stepIndex])

  useAsyncEffect(async () => {
    const doc = await getDocument(uid, id)
    setDocument(doc)

    const data = await getLikeStatus(+id)
    setLiked(data.user_like_status)
  }, [id, uid])

  useAsyncEffect(async () => {
    if (!devList?.length && bindDeviceButtons?.length) return
    const devs = await getDevList()
    setDevList(devs)
  }, [bindDeviceButtons])

  useEffect(() => {
    if (!hideNavBar) {
      setNavigationBar({
        title: document?.title || '',
        onBack: async () => {
          trackDetailBack(uid || id)
          trackViewPercent()
          // 如果在App环境下，调用原生的返回方法
          if (await isInApp()) {
            backToNative()
          } else {
            if (window.history.length > 1) {
              window.history.back()
            }
          }
        },
      })
    }
  }, [id, trackViewPercent, document, uid, hideNavBar])

  // useEffect(() => {
  //   if (scroller?.top && scroller.top > 0) {
  //     togglePullToRefresh(false)
  //   } else {
  //     togglePullToRefresh(true)
  //   }
  //   return () => {
  //     togglePullToRefresh(false)
  //   }
  // }, [scroller?.top])

  // 页面访问埋点
  useEffect(() => {
    // start_ai_generated
    async function handleEffect() {
      // 如果在App环境下，关闭下拉刷新
      if (await isInApp()) {
        togglePullToRefresh(false)
      }
      trackDetailPageView(uid || id, pn as string)
    }
    // end_ai_generated

    // 调用异步函数
    handleEffect()
  }, [id, pn, uid])

  if (!document) {
    return null
  }

  return (
    <div className={`${styles.container} ${hideNavBar ? styles.hideNavBar : ''}`} ref={scrollContanier} id="container">
      <Helmet>
        <title>{document?.title}</title>
      </Helmet>

      <section className={styles.banner}>
        <Swiper indicator={() => null} ref={bannerSwiper} onIndexChange={handleBannerSwipe}>
          {filterSteps?.map((item, index) => (
            <Swiper.Item key={index}>
              {item?.banner ? (
                <img src={item?.banner} alt="banner" />
              ) : (
                isYouTubeVideo(item?.YoutubeUrl || '') ? (
                  <Video
                    key="1"
                    value={item?.YoutubeUrl ?? ''}
                    title=""
                    art_id={+id}
                    description={null}
                    id={1}
                    layout="horizontal"
                    module={'global.tutorial-video-tab'}
                    isPlaying={index !== stepIndex}
                  />
                ) : (
                  <NativeVideo
                    key="1"
                    value={item?.YoutubeUrl ?? ''}
                    title=""
                    art_id={+id}
                    description={null}
                    id={1}
                    layout="horizontal"
                    module={'global.tutorial-video-tab'}
                    isPlaying={index !== stepIndex}
                  />
                )
              )}
            </Swiper.Item>
          ))}
        </Swiper>
      </section>

      {document?.steps.length && document?.steps.length > 1 && (
        <section className={styles.steps}>
          <div className={styles.stepsInner} ref={stepsRef}>
            {document.steps.map((_, i) => (
              <React.Fragment key={i}>
                <div
                  className={clsx(styles.item, { [styles.active]: i <= stepIndex })}
                  onClick={() => handleStepItemClick(i)}
                >
                  {i + 1}
                </div>
                {i !== (document.steps?.length || 1) - 1 && <i className={styles.line}></i>}
              </React.Fragment>
            ))}
          </div>
        </section>
      )}
      <section className={styles.content} ref={contentDom}>
        <Swiper indicator={() => null} onIndexChange={handleBannerSwipe} ref={containerSwiper}>
          {filterSteps?.map((step, index) => (
            <Swiper.Item key={index}>
              <CKEContent __html={step.content} />

              {shouldShowLike && document.feedback && index === (document.steps?.length || 1) - 1 && (
                <section className={styles.feedback}>
                  <p>Do you find this useful?</p>
                  <div className={styles.ops}>
                    <button className={clsx(styles.like, { [styles.active]: liked })} onClick={handleLike}></button>
                    {/* <button className={styles.unlike}></button> */}
                  </div>
                </section>
              )}

              {!!step.action_sheets?.length && (
                <section className={styles.links}>
                  <p>
                    <i></i> Still have problems?
                    <span onClick={() => handleLinksSheetClick(step.action_sheets)}> Click HERE</span>
                  </p>
                </section>
              )}

              {!!step.buttons?.length && (
                <section className={styles.buttons}>
                  {step.buttons?.map(item =>
                    item.url ? (
                      <button key={item.id} onClick={() => handleButtonClick(item)}>
                        {item.title}
                      </button>
                    ) : null
                  )}
                </section>
              )}
            </Swiper.Item>
          ))}
        </Swiper>
      </section>

      <ActionSheet
        visible={linksSheetVisible}
        close={closeLinksSheetVisible}
        actions={currentActions}
        track={title => trackDetailLinkClick(uid || id, title)}
      />
    </div>
  )
}

export default DetailPage
